import React, { useEffect, useState } from 'react';
import {
  Shield, Clock, CheckCircle, XCircle, Eye, ChevronDown, ChevronRight,
  FileText, Calendar, User, AlertTriangle, ExternalLink, RefreshCw,
  UserPlus, Search, Plus
} from 'lucide-react';
import { useApi } from './DashboardLayout';

const STATUS_BADGES = {
  pending: { label: 'Offen', bg: '#FEF3C7', color: '#92400E', icon: Clock },
  approved: { label: 'Genehmigt', bg: '#D1FAE5', color: '#065F46', icon: CheckCircle },
  rejected: { label: 'Abgelehnt', bg: '#FEE2E2', color: '#991B1B', icon: XCircle },
  active: { label: 'Aktiv', bg: '#D1FAE5', color: '#065F46', icon: CheckCircle },
  expired: { label: 'Abgelaufen', bg: '#FEE2E2', color: '#991B1B', icon: AlertTriangle },
  revoked: { label: 'Widerrufen', bg: '#F3F4F6', color: '#374151', icon: XCircle },
};

function StatusBadge({ status }) {
  const s = STATUS_BADGES[status] || { label: status, bg: '#F3F4F6', color: '#374151', icon: Clock };
  const Icon = s.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700,
      padding: '2px 10px', borderRadius: 12, background: s.bg, color: s.color,
    }} data-testid={`gbm-status-${status}`}>
      <Icon size={12} /> {s.label}
    </span>
  );
}

function formatDate(d) {
  if (!d) return '–';
  try { return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return d; }
}

export default function GbmManagementPage() {
  const { apiFetch } = useApi();
  const [tab, setTab] = useState('pending'); // pending | all | permissions
  const [applications, setApplications] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedApp, setExpandedApp] = useState(null);

  // Approve dialog
  const [approveApp, setApproveApp] = useState(null);
  const [approveDate, setApproveDate] = useState('');

  // Reject dialog
  const [rejectApp, setRejectApp] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // Extend dialog
  const [extendPerm, setExtendPerm] = useState(null);
  const [extendDate, setExtendDate] = useState('');

  // Grant dialog (manual)
  const [showGrantDialog, setShowGrantDialog] = useState(false);
  const [grantProviders, setGrantProviders] = useState([]);
  const [grantSearch, setGrantSearch] = useState('');
  const [grantSelected, setGrantSelected] = useState(null);
  const [grantDate, setGrantDate] = useState('');
  const [grantNotes, setGrantNotes] = useState('');
  const [loadingProviders, setLoadingProviders] = useState(false);

  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => { loadData(); }, [tab]);

  const loadData = async () => {
    setLoading(true);
    if (tab === 'permissions') {
      const r = await apiFetch('/api/gbm/permissions');
      if (r?.ok) setPermissions(await r.json());
    } else {
      const status = tab === 'pending' ? 'pending' : '';
      const r = await apiFetch(`/api/gbm/applications${status ? `?status=${status}` : ''}`);
      if (r?.ok) setApplications(await r.json());
    }
    setLoading(false);
  };

  const openCertificate = async (appId) => {
    const r = await apiFetch(`/api/gbm/applications/${appId}/certificate-link`);
    if (r?.ok) {
      const d = await r.json();
      if (d?.url) window.open(d.url, '_blank');
    }
  };

  const openGrantDialog = async () => {
    setShowGrantDialog(true);
    setGrantSelected(null);
    setGrantSearch('');
    setGrantDate(defaultValidUntil());
    setGrantNotes('');
    setLoadingProviders(true);
    const r = await apiFetch('/api/gbm/providers');
    if (r?.ok) setGrantProviders(await r.json());
    setLoadingProviders(false);
  };

  const doGrant = async () => {
    if (!grantSelected) return;
    setActionLoading(true);
    const r = await apiFetch('/api/gbm/permissions/grant', {
      method: 'POST',
      body: JSON.stringify({
        provider_id: grantSelected.id,
        valid_until: grantDate,
        notes: grantNotes,
      })
    });
    if (r?.ok) {
      setShowGrantDialog(false);
      await loadData();
    } else {
      const err = await r?.json().catch(() => null);
      alert(err?.detail || 'Fehler');
    }
    setActionLoading(false);
  };

  const filteredGrantProviders = grantProviders.filter(p =>
    grantSearch === '' ||
    (p.verein || '').toLowerCase().includes(grantSearch.toLowerCase()) ||
    (p.email || '').toLowerCase().includes(grantSearch.toLowerCase()) ||
    String(p.club_id || '').includes(grantSearch)
  );

  const doApprove = async () => {
    if (!approveApp) return;
    setActionLoading(true);
    const r = await apiFetch(`/api/gbm/applications/${approveApp.id}/approve`, {
      method: 'POST', body: JSON.stringify({ valid_until: approveDate })
    });
    if (r?.ok) { setApproveApp(null); await loadData(); }
    else { const err = await r?.json().catch(() => null); alert(err?.detail || 'Fehler'); }
    setActionLoading(false);
  };

  const doReject = async () => {
    if (!rejectApp) return;
    setActionLoading(true);
    const r = await apiFetch(`/api/gbm/applications/${rejectApp.id}/reject`, {
      method: 'POST', body: JSON.stringify({ reason: rejectReason })
    });
    if (r?.ok) { setRejectApp(null); setRejectReason(''); await loadData(); }
    else { const err = await r?.json().catch(() => null); alert(err?.detail || 'Fehler'); }
    setActionLoading(false);
  };

  const doExtend = async () => {
    if (!extendPerm) return;
    setActionLoading(true);
    const r = await apiFetch(`/api/gbm/permissions/${extendPerm.provider_id}`, {
      method: 'PUT', body: JSON.stringify({ valid_until: extendDate, status: 'active' })
    });
    if (r?.ok) { setExtendPerm(null); await loadData(); }
    else { const err = await r?.json().catch(() => null); alert(err?.detail || 'Fehler'); }
    setActionLoading(false);
  };

  const doRevoke = async (perm) => {
    if (!window.confirm(`GBM-Berechtigung für "${perm.provider_name}" wirklich widerrufen?`)) return;
    await apiFetch(`/api/gbm/permissions/${perm.provider_id}`, { method: 'DELETE' });
    await loadData();
  };

  // Default date: 2 years from now
  const defaultValidUntil = () => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 2);
    return d.toISOString().split('T')[0];
  };

  const tabs = [
    { key: 'pending', label: 'Offene Anträge' },
    { key: 'all', label: 'Alle Anträge' },
    { key: 'permissions', label: 'Berechtigungen' },
  ];

  return (
    <div data-testid="gbm-management-page">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div className="page-header" style={{ marginBottom: 0 }}>
          <h2><Shield size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />GBM-Verwaltung</h2>
          <p>Gesundheitsbildende Maßnahmen — Anträge und Berechtigungen</p>
        </div>
        <button className="btn btn-primary" onClick={openGrantDialog}
          data-testid="grant-gbm-btn"
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, flexShrink: 0 }}>
          <UserPlus size={15} /> Berechtigung erteilen
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '2px solid var(--border)', paddingBottom: 0 }}>
        {tabs.map(t => (
          <button key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`gbm-tab-${t.key}`}
            style={{
              padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              border: 'none', background: 'none', color: tab === t.key ? 'var(--primary)' : 'var(--text-muted)',
              borderBottom: tab === t.key ? '2px solid var(--primary)' : '2px solid transparent',
              marginBottom: -2, transition: 'all 0.15s',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>Laden...</div>
      ) : tab === 'permissions' ? (
        /* ═══ PERMISSIONS TABLE ═══ */
        permissions.length === 0 ? (
          <div className="of-card" style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
            <Shield size={32} style={{ opacity: 0.2, marginBottom: 8 }} />
            <p style={{ margin: 0, fontSize: 13 }}>Keine GBM-Berechtigungen vorhanden.</p>
          </div>
        ) : (
          <div className="of-card" style={{ padding: 0 }}>
            <div className="table-wrap" style={{ margin: 0 }}>
              <table data-testid="gbm-permissions-table">
                <thead>
                  <tr>
                    <th>Anbieter</th>
                    <th>Status</th>
                    <th>Gültig bis</th>
                    <th>Genehmigt von</th>
                    <th style={{ width: 140 }}>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {permissions.map(p => {
                    const today = new Date().toISOString().split('T')[0];
                    const isExpired = p.status === 'expired' || (p.status === 'active' && p.valid_until < today);
                    return (
                      <tr key={p.provider_id} data-testid={`gbm-perm-${p.provider_id}`}>
                        <td>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{p.provider_name || '–'}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.provider_email}</div>
                        </td>
                        <td><StatusBadge status={isExpired ? 'expired' : p.status} /></td>
                        <td style={{ fontWeight: 500, color: isExpired ? '#DC2626' : 'var(--text-primary)' }}>
                          {formatDate(p.valid_until)}
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{p.approved_by || '–'}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="btn btn-secondary btn-sm" style={{ fontSize: 11 }}
                              onClick={() => { setExtendPerm(p); setExtendDate(defaultValidUntil()); }}
                              data-testid={`extend-perm-${p.provider_id}`}>
                              <RefreshCw size={12} /> Verlängern
                            </button>
                            {p.status === 'active' && (
                              <button className="btn btn-secondary btn-sm" style={{ fontSize: 11, color: '#991B1B' }}
                                onClick={() => doRevoke(p)}
                                data-testid={`revoke-perm-${p.provider_id}`}>
                                Widerrufen
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )
      ) : (
        /* ═══ APPLICATIONS LIST ═══ */
        applications.length === 0 ? (
          <div className="of-card" style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
            <FileText size={32} style={{ opacity: 0.2, marginBottom: 8 }} />
            <p style={{ margin: 0, fontSize: 13 }}>
              {tab === 'pending' ? 'Keine offenen GBM-Anträge.' : 'Keine GBM-Anträge vorhanden.'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {applications.map(app => {
              const isExpanded = expandedApp === app.id;
              return (
                <div key={app.id} className="of-card" style={{ padding: 0, overflow: 'hidden' }}
                  data-testid={`gbm-app-${app.id}`}>
                  {/* Header */}
                  <div style={{
                    display: 'flex', alignItems: 'center', padding: '14px 16px', cursor: 'pointer',
                    gap: 12, transition: 'background 0.15s',
                  }}
                    onClick={() => setExpandedApp(isExpanded ? null : app.id)}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary, #f9fafb)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    {isExpanded ? <ChevronDown size={16} style={{ color: 'var(--text-muted)' }} /> :
                      <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{app.provider_name || 'Unbekannt'}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        Antrag #{app.id} — eingereicht am {formatDate(app.submitted_at)}
                      </div>
                    </div>
                    <StatusBadge status={app.status} />
                  </div>

                  {/* Body */}
                  {isExpanded && (
                    <div style={{ borderTop: '1px solid var(--border)', padding: '16px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                        <div>
                          <label className="of-label">Teilnehmer</label>
                          <div style={{ fontSize: 14, fontWeight: 500 }}>{app.participant_name || '–'}</div>
                        </div>
                        <div>
                          <label className="of-label">Schulungsdatum</label>
                          <div style={{ fontSize: 14, fontWeight: 500 }}>{formatDate(app.training_date)}</div>
                        </div>
                        <div>
                          <label className="of-label">E-Mail</label>
                          <div style={{ fontSize: 14, fontWeight: 500 }}>{app.provider_email || '–'}</div>
                        </div>
                      </div>

                      {app.notes && (
                        <div style={{ marginBottom: 16 }}>
                          <label className="of-label">Anmerkung</label>
                          <div style={{ fontSize: 13, lineHeight: 1.5, padding: '8px 12px', background: 'var(--bg-secondary, #f9fafb)', borderRadius: 6 }}>
                            {app.notes}
                          </div>
                        </div>
                      )}

                      {/* Certificate */}
                      <div style={{ marginBottom: 16 }}>
                        <label className="of-label">Zertifikat</label>
                        <button className="btn btn-secondary btn-sm" onClick={() => openCertificate(app.id)}
                          data-testid={`view-cert-${app.id}`}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                          <Eye size={14} /> {app.certificate_filename || 'Zertifikat anzeigen'}
                          <ExternalLink size={12} style={{ opacity: 0.5 }} />
                        </button>
                      </div>

                      {app.status === 'rejected' && app.rejection_reason && (
                        <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 8, background: '#FEF2F2', border: '1px solid #FECACA' }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#991B1B', marginBottom: 4 }}>Ablehnungsgrund:</div>
                          <div style={{ fontSize: 13, color: '#991B1B' }}>{app.rejection_reason}</div>
                        </div>
                      )}

                      {app.reviewed_at && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 16 }}>
                          Bearbeitet am {formatDate(app.reviewed_at)} von {app.reviewed_by || '–'}
                        </div>
                      )}

                      {/* Action buttons (only for pending) */}
                      {app.status === 'pending' && (
                        <div style={{ display: 'flex', gap: 8, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                          <button className="btn btn-primary btn-sm"
                            onClick={() => { setApproveApp(app); setApproveDate(defaultValidUntil()); }}
                            data-testid={`approve-app-${app.id}`}
                            style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <CheckCircle size={14} /> Freischalten
                          </button>
                          <button className="btn btn-secondary btn-sm"
                            onClick={() => { setRejectApp(app); setRejectReason(''); }}
                            data-testid={`reject-app-${app.id}`}
                            style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#991B1B' }}>
                            <XCircle size={14} /> Ablehnen
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ═══ APPROVE DIALOG ═══ */}
      {approveApp && (
        <ModalOverlay onClose={() => setApproveApp(null)}>
          <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700 }}>GBM-Berechtigung erteilen</h3>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--text-muted)' }}>
            Für: <strong>{approveApp.provider_name}</strong>
          </p>
          <label className="of-label">Gültig bis</label>
          <input className="of-input" type="date" value={approveDate} onChange={e => setApproveDate(e.target.value)}
            data-testid="approve-date-input" style={{ marginBottom: 16 }} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setApproveApp(null)}>Abbrechen</button>
            <button className="btn btn-primary btn-sm" onClick={doApprove} disabled={actionLoading || !approveDate}
              data-testid="confirm-approve-btn">
              {actionLoading ? 'Wird freigeschalten...' : 'Freischalten'}
            </button>
          </div>
        </ModalOverlay>
      )}

      {/* ═══ REJECT DIALOG ═══ */}
      {rejectApp && (
        <ModalOverlay onClose={() => setRejectApp(null)}>
          <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700 }}>GBM-Antrag ablehnen</h3>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--text-muted)' }}>
            Für: <strong>{rejectApp.provider_name}</strong>
          </p>
          <label className="of-label">Begründung (optional)</label>
          <textarea className="of-input" rows={3} value={rejectReason} onChange={e => setRejectReason(e.target.value)}
            placeholder="Optional: Grund der Ablehnung..." data-testid="reject-reason-input" style={{ marginBottom: 16 }} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setRejectApp(null)}>Abbrechen</button>
            <button className="btn btn-primary btn-sm" onClick={doReject} disabled={actionLoading}
              data-testid="confirm-reject-btn" style={{ color: '#fff', background: '#DC2626' }}>
              {actionLoading ? 'Wird abgelehnt...' : 'Ablehnen'}
            </button>
          </div>
        </ModalOverlay>
      )}

      {/* ═══ EXTEND DIALOG ═══ */}
      {extendPerm && (
        <ModalOverlay onClose={() => setExtendPerm(null)}>
          <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700 }}>Berechtigung verlängern</h3>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--text-muted)' }}>
            Für: <strong>{extendPerm.provider_name}</strong> — aktuell gültig bis {formatDate(extendPerm.valid_until)}
          </p>
          <label className="of-label">Neues Ablaufdatum</label>
          <input className="of-input" type="date" value={extendDate} onChange={e => setExtendDate(e.target.value)}
            data-testid="extend-date-input" style={{ marginBottom: 16 }} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setExtendPerm(null)}>Abbrechen</button>
            <button className="btn btn-primary btn-sm" onClick={doExtend} disabled={actionLoading || !extendDate}
              data-testid="confirm-extend-btn">
              {actionLoading ? 'Wird verlängert...' : 'Verlängern'}
            </button>
          </div>
        </ModalOverlay>
      )}

      {/* ═══ GRANT DIALOG ═══ */}
      {showGrantDialog && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }} onClick={() => setShowGrantDialog(false)} data-testid="grant-dialog-overlay">
          <div style={{
            background: 'var(--bg-primary, #fff)', borderRadius: 12, width: '100%', maxWidth: 520,
            maxHeight: '80vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border)' }}>
              <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700 }}>
                <UserPlus size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                GBM-Berechtigung erteilen
              </h3>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
                Anbieter auswählen und Laufzeit festlegen
              </p>
            </div>

            {/* Body */}
            <div style={{ padding: '16px 24px', overflowY: 'auto', flex: 1 }}>
              {/* Provider search + select */}
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 6 }}>Anbieter auswählen</label>

              {grantSelected ? (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                  background: '#F0FDF4', borderRadius: 8, border: '1px solid #BBF7D0', marginBottom: 16,
                }}>
                  <CheckCircle size={16} style={{ color: '#16A34A', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{grantSelected.verein || '–'}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{grantSelected.email}</div>
                  </div>
                  <button className="btn-icon" onClick={() => setGrantSelected(null)} style={{ color: 'var(--text-muted)' }}>
                    <XCircle size={16} />
                  </button>
                </div>
              ) : (
                <>
                  <div style={{ position: 'relative', marginBottom: 8 }}>
                    <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                    <input className="of-input" value={grantSearch} onChange={e => setGrantSearch(e.target.value)}
                      placeholder="Verein, E-Mail oder Club-ID suchen..."
                      style={{ paddingLeft: 32, fontSize: 12 }} data-testid="grant-provider-search" />
                  </div>
                  <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 6, marginBottom: 16 }}>
                    {loadingProviders ? (
                      <div style={{ padding: 16, textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>Laden...</div>
                    ) : filteredGrantProviders.length === 0 ? (
                      <div style={{ padding: 16, textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>
                        {grantSearch ? 'Kein Anbieter gefunden' : 'Keine Anbieter vorhanden'}
                      </div>
                    ) : (
                      filteredGrantProviders.map(p => (
                        <div key={p.id} style={{
                          display: 'flex', alignItems: 'center', padding: '8px 12px',
                          borderBottom: '1px solid var(--border)', gap: 10, cursor: 'pointer',
                          transition: 'background 0.1s',
                        }}
                          onClick={() => setGrantSelected(p)}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary, #f9fafb)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          data-testid={`grant-select-${p.id}`}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 500, fontSize: 13 }}>{p.verein || '–'}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.email}</div>
                          </div>
                          {p.gbm_status === 'active' && (
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 8, background: '#D1FAE5', color: '#065F46' }}>
                              Aktiv
                            </span>
                          )}
                          {p.gbm_status === 'expired' && (
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 8, background: '#FEE2E2', color: '#991B1B' }}>
                              Abgelaufen
                            </span>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}

              {/* Valid until */}
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 6 }}>Gültig bis</label>
              <input className="of-input" type="date" value={grantDate} onChange={e => setGrantDate(e.target.value)}
                data-testid="grant-date-input" style={{ marginBottom: 16 }} />

              {/* Notes */}
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 6 }}>Anmerkung (optional)</label>
              <textarea className="of-input" rows={2} value={grantNotes} onChange={e => setGrantNotes(e.target.value)}
                placeholder="z.B. Schulung am 15.03.2026 absolviert"
                data-testid="grant-notes-input" style={{ marginBottom: 8 }} />
            </div>

            {/* Footer */}
            <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowGrantDialog(false)}>Abbrechen</button>
              <button className="btn btn-primary btn-sm" onClick={doGrant}
                disabled={actionLoading || !grantSelected || !grantDate}
                data-testid="confirm-grant-btn">
                {actionLoading ? 'Wird erteilt...' : 'Berechtigung erteilen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function ModalOverlay({ children, onClose }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={onClose} data-testid="gbm-modal-overlay">
      <div style={{
        background: 'var(--bg-primary, #fff)', borderRadius: 12, width: '100%', maxWidth: 420,
        padding: '24px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
      }} onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}
